import React, { FC, useState, useMemo, useCallback, Fragment } from 'react';
import { FieldProps, Field } from 'formik';
import styled from 'styled-components';
import _some from 'lodash/some';
import _every from 'lodash/every';
import _includes from 'lodash/includes';
import _filter from 'lodash/filter';
import _map from 'lodash/map';
import _uniqBy from 'lodash/uniqBy';
import { useTranslation } from '@blocs.i18n';
import Loader from '@minecraft.loader';
import { gqlFilterOperator, gqlSearchResultType } from '@minecraft.graphql-types';
import { setFieldValueAndTouched, haveSameContents, helpers } from '@minecraft.utils';
import { SelectOption } from '@minecraft.typings';
import { CheckboxListWithSearchProps } from '../shared/types';
import CheckboxItem from '../CheckboxItem';
import CheckboxList from './CheckboxList';

const { getAllNestedOptions } = helpers;

const SELECT_ALL = 'SELECT_ALL';

const SelectAllCheckbox = styled(CheckboxItem)`
  border-bottom: 0.0625px solid ${(props): string => props.theme.form.borderColor};
`;

const StyledFieldLabel = styled.div`
  border-bottom: 0.0625px solid ${(props): string => props.theme.form.borderColor};
  font-size: 1rem;
  text-align: inherit;
  padding: 0.5rem;
  width: 100%;
`;

const StyledEmptyResult = styled.div`
  color: ${(props): string => props.theme.colors.primary.grey2};
  font-size: 0.875rem;
  text-align: center;
`;

interface Props extends Partial<CheckboxListWithSearchProps> {
  formikSelectedOptions?: SelectOption[];
  initialSelectedOptions?: SelectOption[];
  form: FieldProps['form'];
  selectedSkillOperator?: gqlFilterOperator;
}

const CheckboxListContainer: FC<Props> = ({
  withFormik,
  withSelectAll,
  isLoading,
  name,
  form,
  options,
  formikSelectedOptions = [],
  initialSelectedOptions = [],
  initialDisabledOptions = [],
  onOptionsChange,
  appliedQueryInput,
  refetchProfileFilterData,
  renderFieldLabel: renderFieldLabelProp,
  selectedSkillOperator = gqlFilterOperator.OR,
}) => {
  const { t } = useTranslation();

  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions);
  const [collapsedParentOptions, setCollapsedParentOptions] = useState([]);

  const allNestedOptions = useMemo(() => getAllNestedOptions(options), [options]);
  const currentSelectedOptions = useMemo(() => {
    return withFormik ? formikSelectedOptions : selectedOptions;
  }, [withFormik, formikSelectedOptions, selectedOptions]);

  const disabledOptions = useMemo(() => {
    const allNestedOptionsWithZeroCount = _filter(allNestedOptions, { count: 0 });

    return _uniqBy([...initialDisabledOptions, ...allNestedOptionsWithZeroCount], 'value');
  }, [initialDisabledOptions, allNestedOptions]);

  const getEnabledOptions = useCallback(
    (originalOptions: SelectOption[]): SelectOption[] => {
      return _filter(originalOptions, (item) => !_some(disabledOptions, { value: item.value }));
    },
    [disabledOptions]
  );

  const isSomeOptionsWithFieldName = useMemo(() => {
    return _some(options, (option) => option.fieldName);
  }, [options]);
  const areAllOptionsSelected = useMemo(() => {
    const enabledOptions = getEnabledOptions(allNestedOptions);

    return enabledOptions?.length > 0 ? haveSameContents(enabledOptions, currentSelectedOptions) : false;
  }, [getEnabledOptions, currentSelectedOptions]);
  const areAllOptionsDisabled = useMemo(() => {
    return disabledOptions?.length > 0 ? haveSameContents(disabledOptions, allNestedOptions) : false;
  }, [disabledOptions, allNestedOptions]);

  const handleCheckboxItemSelect = (newOptions: SelectOption[]): void => {
    const enabledNewOptions = getEnabledOptions(newOptions);
    const newOptionsValue = _map(enabledNewOptions, 'value');
    const areNewOptionsExist =
      enabledNewOptions?.length > 0
        ? _every(newOptionsValue, (newItemValue) => _includes(_map(currentSelectedOptions, 'value'), newItemValue))
        : _some(currentSelectedOptions, (selectedItem) => _includes(newOptionsValue, selectedItem.value));
    const updatedSelectedOptions = areNewOptionsExist
      ? _filter(currentSelectedOptions, (selectedItem) => !_includes(newOptionsValue, selectedItem.value))
      : [...currentSelectedOptions, ...enabledNewOptions];

    if (withFormik) {
      setFieldValueAndTouched(name, updatedSelectedOptions, form);
    } else {
      setSelectedOptions(updatedSelectedOptions);
    }

    if (onOptionsChange) {
      onOptionsChange(updatedSelectedOptions);
    }

    if (refetchProfileFilterData) {
      const ids = updatedSelectedOptions.map((item) => ({ id: item.value }));

      refetchProfileFilterData({
        input: {
          profileStatusToSearch: appliedQueryInput?.profileStatusToSearch,
          skills: ids,
          skillOperator: selectedSkillOperator?.value || selectedSkillOperator || appliedQueryInput?.skillOperator,
        },
        resultType: gqlSearchResultType.FACETS,
      });
    }
  };

  const toggleSelectAll = (): void => {
    let updatedSelectedOptions;

    if (areAllOptionsSelected) {
      updatedSelectedOptions = [];
    } else {
      const enabledOptions = getEnabledOptions(allNestedOptions);

      updatedSelectedOptions = enabledOptions;
    }

    if (withFormik) {
      setFieldValueAndTouched(name, updatedSelectedOptions, form);
    } else {
      setSelectedOptions(updatedSelectedOptions);
    }

    if (onOptionsChange) {
      onOptionsChange(updatedSelectedOptions);
    }
  };

  const toggleParentOptions = (parentItem: SelectOption): void => {
    let updatedCollapsedParentItens;

    if (_some(collapsedParentOptions, { value: parentItem.value })) {
      updatedCollapsedParentItens = _filter(
        collapsedParentOptions,
        (collapsedParentItem) => collapsedParentItem.value !== parentItem.value
      );
    } else {
      updatedCollapsedParentItens = [...collapsedParentOptions, parentItem];
    }

    setCollapsedParentOptions(updatedCollapsedParentItens);
  };

  const renderFieldLabel = (fieldLabel: string): JSX.Element => {
    if (!fieldLabel) {
      return null;
    }

    if (renderFieldLabelProp) {
      return renderFieldLabelProp(fieldLabel);
    }

    return <StyledFieldLabel>{fieldLabel}</StyledFieldLabel>;
  };

  const groupedOptionsByFieldName = helpers.getGroupedOptionsByFieldName(isSomeOptionsWithFieldName, options);

  return (
    <div>
      {isLoading && <Loader />}
      {!options?.length && !isLoading && <StyledEmptyResult>{t('common:dropdown.noResults')}</StyledEmptyResult>}
      {options?.length > 0 && !isLoading && (
        <>
          {withSelectAll && (
            <SelectAllCheckbox
              isItemSelected={areAllOptionsSelected}
              isItemDisabled={areAllOptionsDisabled}
              checkboxEnable
              item={{ value: SELECT_ALL, label: t('common:form.label.selectAll') }}
              onCheckboxItemSelect={toggleSelectAll}
            />
          )}
          {isSomeOptionsWithFieldName ? (
            _map(groupedOptionsByFieldName.values, (groupedOption) => (
              <Fragment key={groupedOption.fieldName}>
                {renderFieldLabel(groupedOption?.fieldLabel)}
                <CheckboxList
                  options={groupedOption.options}
                  selectedOptions={currentSelectedOptions}
                  disabledOptions={disabledOptions}
                  collapsedParentOptions={collapsedParentOptions}
                  toggleParentOptions={toggleParentOptions}
                  handleCheckboxItemSelect={handleCheckboxItemSelect}
                />
              </Fragment>
            ))
          ) : (
            <CheckboxList
              options={options}
              selectedOptions={currentSelectedOptions}
              disabledOptions={disabledOptions}
              collapsedParentOptions={collapsedParentOptions}
              toggleParentOptions={toggleParentOptions}
              handleCheckboxItemSelect={handleCheckboxItemSelect}
            />
          )}
        </>
      )}
    </div>
  );
};

export default function ({ withFormik, name, initialSelectedOptions, ...restProps }) {
  return withFormik ? (
    <Field name={name}>
      {({ field, form }) => (
        <CheckboxListContainer
          withFormik={withFormik}
          name={name}
          formikSelectedOptions={field.value}
          selectedSkillOperator={form.values['skillOperator']}
          form={form}
          {...restProps}
        />
      )}
    </Field>
  ) : (
    <CheckboxListContainer withFormik={withFormik} initialSelectedOptions={initialSelectedOptions} {...restProps} />
  );
}
